// Presentational home advertising banner powered by the Kevel (HBX) ad-server.
// Renders an empty anchor div (so the IntersectionObserver always has a target,
// per useOnScreen's contract) and the actual banner content only when an ad is
// available. Fires the Kevel impression pixel + a `view` analytics event when
// first visible, and a `click` event when the user clicks through.
import { memo, useEffect, useRef } from "react";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { AdvertisingDecision } from "app/reducers/advertisingActionCreators";
import { AdvertisingStatus } from "app/reducers/advertisingConstants";
import "./HomeAdvertisingBanner.scss";

const TRACK_VIEW = "home_advertising_banner_view";
const TRACK_CLICK = "home_advertising_banner_click";
const TRACK_API_STATUS = "home_advertising_banner_api_status";

// GET via <img> bypasses CORS and is harder to block than fetch().
const fireImpression = (url: string): void => {
	const pixel = new Image();
	pixel.src = url;
};

type Props = {
	ad: AdvertisingDecision | null;
	status: AdvertisingStatus | null;
};

const HomeAdvertisingBanner = ({ ad, status }: Props) => {
	const { track } = useAnalytics();
	// `track` is recreated by useAnalytics() on every render; keep a stable ref
	// so the effects below don't re-fire on unrelated re-renders.
	const trackRef = useRef(track);
	trackRef.current = track;

	const ref = useRef<HTMLDivElement>(null);
	const isVisible = useOnScreen({ ref, triggerOnce: true });

	// Report the SSR call outcome once per page load.
	useEffect(() => {
		if (status) {
			trackRef.current(TRACK_API_STATUS, { status });
		}
	}, [status]);

	// Count one impression (Kevel + analytics) when the banner becomes visible.
	useEffect(() => {
		if (isVisible && ad) {
			trackRef.current(TRACK_VIEW, { adId: ad.adId, campaignId: ad.campaignId });
			fireImpression(ad.impressionUrl);
		}
	}, [isVisible, ad]);

	const handleClick = () => {
		if (ad) {
			trackRef.current(TRACK_CLICK, { adId: ad.adId, campaignId: ad.campaignId });
		}
	};

	const firstContent = ad ? ad.contents[0] : undefined;
	const image = firstContent ? firstContent.data : undefined;
	const canRender = ad && image && image.imageUrl;

	return (
		<div ref={ref} className="home-advertising-banner">
			{canRender && (
				<a
					className="home-advertising-banner__link"
					href={ad.clickUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleClick}
				>
					<img
						className="home-advertising-banner__image"
						src={image.imageUrl}
						alt={image.title || ""}
						width={image.width}
						height={image.height}
						loading="lazy"
					/>
				</a>
			)}
		</div>
	);
};

export default memo(HomeAdvertisingBanner);
